<template>
	<div class="min-h-screen bg-gray-50">
		<div class="bg-white border-b border-gray-200 py-4 px-6">
			<h1 class="text-2xl font-semibold text-gray-900 m-0">Themes</h1>
		</div>

		<div class="p-6">
			<div class="bg-white rounded-lg shadow-sm overflow-hidden">
				<div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900 m-0">All Themes</h2>
					<button
						class="bg-violet-600 hover:bg-violet-700 text-white border-none rounded-md py-2 px-4 text-sm font-medium cursor-pointer"
						@click="showCreateModal = true"
					>
						+ Add Theme
					</button>
				</div>

				<div class="flex justify-between items-center py-4 px-6 border-b border-gray-200 gap-4 flex-wrap">
					<div class="flex-1 min-w-[200px]">
						<input
							v-model="searchQuery"
							type="text"
							placeholder="Search themes..."
							class="w-full max-w-[300px] py-2 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
						>
					</div>
					<div class="flex items-center gap-2">
						<label class="text-sm text-gray-500">Offering:</label>
						<select v-model="selectedOfferingId" class="py-2 px-3 border border-gray-300 rounded-md text-sm bg-white min-w-[200px]" @change="loadData">
							<option value="">All Offerings</option>
							<option
								v-for="off in offerings"
								:key="off.id"
								:value="off.id"
							>
								{{ off.id }} - {{ off.name }}
							</option>
						</select>
					</div>
					<div class="flex items-center gap-2">
						<label class="text-sm text-gray-500">Show:</label>
						<select v-model="pageSize" class="py-2 px-3 border border-gray-300 rounded-md text-sm bg-white">
							<option :value="10">10</option>
							<option :value="25">25</option>
							<option :value="50">50</option>
							<option :value="100">100</option>
						</select>
					</div>
				</div>

				<div v-if="isLoading" class="py-12 px-6 text-center text-gray-500">
					Loading...
				</div>

				<div v-else class="overflow-x-auto">
					<table class="w-full border-collapse">
						<thead>
							<tr>
								<th class="text-left py-3 px-4 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase w-[80px]">Icon</th>
								<th class="text-left py-3 px-4 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase">ID</th>
								<th class="text-left py-3 px-4 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase">Name</th>
								<th class="text-left py-3 px-4 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase w-[120px]">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="item in paginatedItems"
								:key="item.id"
								class="border-b border-gray-200 hover:bg-gray-50"
							>
								<td class="py-3 px-4 text-sm text-gray-900 align-middle">
									<img
										v-if="item.icon"
										:src="item.icon"
										:alt="item.name"
										class="w-10 h-10 rounded object-cover"
									>
									<div v-else class="w-10 h-10 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xs">-</div>
								</td>
								<td class="py-3 px-4 text-sm text-gray-900 align-middle">{{ item.id }}</td>
								<td class="py-3 px-4 text-sm text-gray-900 align-middle">{{ item.name }}</td>
								<td class="py-3 px-4 text-sm text-gray-900 align-middle">
									<div class="flex gap-2">
										<button
											class="bg-blue-50 hover:bg-blue-100 text-blue-500 border-none rounded py-1.5 px-2.5 cursor-pointer text-sm"
											title="Edit"
											@click="editItem(item.id)"
										>
											<i class="fa fa-edit"></i>
										</button>
										<button
											class="bg-red-50 hover:bg-red-100 text-red-500 border-none rounded py-1.5 px-2.5 cursor-pointer text-sm"
											title="Delete"
											@click="confirmDelete(item)"
										>
											<i class="fa fa-trash"></i>
										</button>
									</div>
								</td>
							</tr>
							<tr v-if="paginatedItems.length === 0">
								<td class="py-3 px-4 text-sm text-gray-900 align-middle" colspan="4">
									<div class="text-center text-gray-500 py-6">No themes found</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div class="flex justify-between items-center py-4 px-6 border-t border-gray-200 flex-wrap gap-4">
					<div class="text-sm text-gray-500">
						Showing {{ startRow }} to {{ endRow }} of {{ totalRows }} entries
					</div>
					<div class="flex items-center gap-2">
						<button
							class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded py-1.5 px-3 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
							:disabled="currentPage === 1"
							@click="goToPage(1)"
						>
							First
						</button>
						<button
							class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded py-1.5 px-3 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
							:disabled="currentPage === 1"
							@click="goToPage(currentPage - 1)"
						>
							Previous
						</button>
						<span class="text-sm text-gray-700 px-2">
							Page {{ currentPage }} of {{ totalPages }}
						</span>
						<button
							class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded py-1.5 px-3 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
							:disabled="currentPage === totalPages"
							@click="goToPage(currentPage + 1)"
						>
							Next
						</button>
						<button
							class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded py-1.5 px-3 text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
							:disabled="currentPage === totalPages"
							@click="goToPage(totalPages)"
						>
							Last
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Create Modal -->
		<div v-if="showCreateModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]" @click.self="showCreateModal = false">
			<div class="bg-white rounded-lg w-full max-w-[400px] m-4 shadow-xl">
				<div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
					<h3 class="text-lg font-semibold text-gray-900 m-0">Create New Theme</h3>
					<button class="bg-transparent border-none text-2xl text-gray-500 hover:text-gray-700 cursor-pointer p-0 leading-none" @click="showCreateModal = false">&times;</button>
				</div>
				<div class="p-6">
					<div class="mb-4">
						<label class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
						<input
							v-model="newThemeName"
							type="text"
							placeholder="Enter theme name"
							class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
						>
					</div>
					<div class="mb-4">
						<label class="block text-sm font-medium text-gray-700 mb-1">Product Group</label>
						<select v-model="newThemeOfferingId" class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500">
							<option value="">-- Select Offering --</option>
							<option
								v-for="off in offerings"
								:key="off.id"
								:value="off.id"
							>
								{{ off.id }} - {{ off.name }} - {{ off.description }}
							</option>
						</select>
					</div>
				</div>
				<div class="flex justify-end gap-3 py-4 px-6 border-t border-gray-200">
					<button
						class="bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-md py-2 px-4 text-sm font-medium cursor-pointer"
						@click="showCreateModal = false"
					>
						Cancel
					</button>
					<button
						class="bg-violet-600 hover:bg-violet-700 text-white border-none rounded-md py-2 px-4 text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
						:disabled="!newThemeName.trim() || isCreating"
						@click="createItem"
					>
						{{ isCreating ? 'Creating...' : 'Create' }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ThemeModel, OfferingModel } from '@sosocio/creator/interfaces';
import Swal from 'sweetalert2';
import ThemeService from 'services/ThemeService';
import { httpClient } from 'utils/http';

export default defineComponent({
	name: 'ThemesPage',
	setup() {
		const router = useRouter();

		const items = ref<ThemeModel[]>([]);
		const offerings = ref<OfferingModel[]>([]);
		const selectedOfferingId = ref<string>('');
		const searchQuery = ref('');
		const currentPage = ref(1);
		const pageSize = ref(25);
		const isLoading = ref(true);
		const showCreateModal = ref(false);
		const newThemeName = ref('');
		const newThemeOfferingId = ref<string>('');
		const isCreating = ref(false);

		const filteredItems = computed(() => {
			if (!searchQuery.value.trim()) {
				return items.value;
			}
			const query = searchQuery.value.toLowerCase();
			return items.value.filter(item =>
				item.name?.toLowerCase().includes(query) ||
				String(item.id).includes(query)
			);
		});

		const totalRows = computed(() => filteredItems.value.length);
		const totalPages = computed(() => Math.max(1, Math.ceil(totalRows.value / pageSize.value)));
		const startRow = computed(() => totalRows.value === 0 ? 0 : (currentPage.value - 1) * pageSize.value + 1);
		const endRow = computed(() => Math.min(currentPage.value * pageSize.value, totalRows.value));

		const paginatedItems = computed(() => {
			const start = (currentPage.value - 1) * pageSize.value;
			return filteredItems.value.slice(start, start + pageSize.value);
		});

		watch([searchQuery, pageSize], () => {
			currentPage.value = 1;
		});

		const goToPage = (page: number) => {
			if (page >= 1 && page <= totalPages.value) {
				currentPage.value = page;
			}
		};

		const loadOfferings = async () => {
			try {
				const response = await httpClient.get<OfferingModel[]>('/api/offering?limit=0');
				offerings.value = response.data;
			} catch (error: any) {
				console.error('Failed to load offerings:', error);
			}
		};

		const loadData = async () => {
			isLoading.value = true;
			try {
				const params = new URLSearchParams({ limit: '0' });
				
				if (selectedOfferingId.value) {
					const offering = offerings.value.find(o => String(o.id) === selectedOfferingId.value);
					if (offering) {
						params.append('where', JSON.stringify({
							groupid: offering.groupid,
							typeid: offering.typeid,
						}));
					}
				}
				
				const data = await ThemeService.getAll(params);
				items.value = data;
			} catch (error: any) {
				console.error('Failed to load themes:', error);
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load themes',
					icon: 'error',
				});
			} finally {
				isLoading.value = false;
			}
		};

		const editItem = (id: number) => {
			router.push(`/theme/${id}`);
		};

		const confirmDelete = async (item: ThemeModel) => {
			const result = await Swal.fire({
				title: 'Delete Theme?',
				text: `Are you sure you want to delete "${item.name || 'this theme'}"? This action cannot be undone.`,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#dc2626',
				cancelButtonColor: '#6b7280',
				confirmButtonText: 'Yes, delete it',
				cancelButtonText: 'Cancel',
			});

			if (result.isConfirmed) {
				try {
					await ThemeService.delete(item.id);
					items.value = items.value.filter(i => i.id !== item.id);
					Swal.fire({
						toast: true,
						position: 'top-end',
						icon: 'success',
						title: 'Theme deleted successfully',
						showConfirmButton: false,
						timer: 3000,
					});
				} catch (error: any) {
					Swal.fire({
						title: 'Error',
						text: error.message || 'Failed to delete theme',
						icon: 'error',
					});
				}
			}
		};

		const createItem = async () => {
			if (!newThemeName.value.trim()) return;

			isCreating.value = true;
			try {
				const payload: any = {
					name: newThemeName.value.trim(),
				};
				
				if (newThemeOfferingId.value) {
					const offering = offerings.value.find(o => String(o.id) === newThemeOfferingId.value);
					if (offering) {
						payload.typeid = offering.typeid;
						payload.groupid = offering.groupid;
					}
				}

				const newTheme = await ThemeService.create(payload);
				showCreateModal.value = false;
				newThemeName.value = '';
				newThemeOfferingId.value = '';
				router.push(`/theme/${newTheme.id}`);
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to create theme',
					icon: 'error',
				});
			} finally {
				isCreating.value = false;
			}
		};

		onMounted(async () => {
			await loadOfferings();
			await loadData();
		});

		return {
			items, offerings, selectedOfferingId, searchQuery, currentPage, pageSize, isLoading,
			showCreateModal, newThemeName, newThemeOfferingId, isCreating,
			filteredItems, totalRows, totalPages, startRow, endRow, paginatedItems,
			goToPage, loadData, editItem, confirmDelete, createItem,
		};
	},
});
</script>
